import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/common/Seo";
import { HEALTH, IMAGES, SEO } from "@/content/site";

const HEALTH_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "WebSite", "@id": "https://azekahealth.com/#website", name: "Azeka Health",
      url: "https://azekahealth.com", publisher: { "@id": "https://azekahealth.com/#organization" } },
    { "@type": "Organization", "@id": "https://azekahealth.com/#organization", name: "Azeka Health",
      url: "https://azekahealth.com",
      parentOrganization: { "@type": "Organization", name: "AZEKA GLOBAL TRADE FZCO", url: "https://azekaglobal.com" } },
  ],
};

const SECTIONS = [
  { h: "What Azeka Health is", b: "Azeka Health is the dedicated platform for health and wellbeing brands owned or controlled by AZEKA GLOBAL TRADE FZCO. It brings together our health-focused brand activities under a single, clearly identified identity." },
  { h: "Relationship to Azeka Global", b: "Azeka Health is not a separate company. It is part of the commercial brand activities of AZEKA GLOBAL TRADE FZCO — the same Dubai-based entity behind Azeka Global, our international sourcing and trade platform." },
  { h: "Ownership & commercialisation model", b: "We develop and commercialise selected health and wellbeing brands owned or controlled by Azeka, working with qualified manufacturing and market partners rather than operating our own production." },
  { h: "Product development & manufacturing approach", b: "Where products are prepared, they are developed and produced with appropriately qualified contract manufacturers under applicable quality standards. Azeka coordinates brand development and market access; it does not claim in-house manufacturing." },
  { h: "Quality philosophy", b: "The same commitment to provenance, documentation and proportionate diligence that defines Azeka Global guides Azeka Health — quality and integrity are built in from the start, not added later." },
  { h: "Future distribution partnerships", b: "As the portfolio is prepared, Azeka Health intends to work with qualified distribution and retail partners in relevant markets, subject to the applicable regulatory requirements of each market." },
];

export default function Health() {
  const reduce = useReducedMotion();
  const anim = (delay) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="page-health" className="relative min-h-screen overflow-hidden bg-azeka-sage">
      <Seo title={SEO.health.t} description={SEO.health.d} path="/health" image="/assets/og/azeka-health.png" siteName="Azeka Health" jsonLd={HEALTH_JSONLD} canonical="https://azekahealth.com" robots="noindex, nofollow" />
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-azeka-teal2/20 blur-3xl animate-float" />
      <div className="pointer-events-none absolute -right-24 top-40 h-80 w-80 rounded-full bg-azeka-emerald/15 blur-3xl animate-float" style={{ animationDelay: "2s" }} />
      <img src={IMAGES.botanical} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-1/2 w-full object-cover opacity-10" />

      <div className="relative mx-auto max-w-4xl px-6 py-20">
        {/* Hero */}
        <div className="flex flex-col items-center text-center">
          <motion.div {...anim(0)} className="rounded-2xl bg-white/80 px-8 py-6 shadow-xl shadow-azeka-navy/5 backdrop-blur-xl">
            <Logo variant="health" className="h-16 w-auto sm:h-20" />
          </motion.div>
          <motion.span {...anim(0.1)} className="mt-8 inline-flex items-center rounded-full border border-azeka-teal/30 bg-white/60 px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] text-azeka-teal backdrop-blur">
            {HEALTH.status}
          </motion.span>
          <motion.h1 {...anim(0.18)} className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-azeka-navy leading-tight">
            {HEALTH.title}
          </motion.h1>
          <motion.p {...anim(0.26)} className="mt-6 max-w-2xl text-lg leading-relaxed text-azeka-navy2">
            {HEALTH.body}
          </motion.p>
        </div>

        {/* Factual ownership statement */}
        <motion.p {...anim(0.32)} className="mx-auto mt-12 max-w-3xl rounded-2xl bg-white/70 px-6 py-5 text-center text-base leading-relaxed text-azeka-navy backdrop-blur" data-testid="health-ownership-statement">
          Azeka Health is the dedicated platform for health and wellbeing brands owned or controlled by
          <strong> AZEKA GLOBAL TRADE FZCO</strong>.
        </motion.p>

        {/* Substantive sections */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {SECTIONS.map((s, i) => (
            <motion.div key={s.h} {...anim(0.36 + i * 0.04)} className="rounded-2xl bg-white/75 p-6 backdrop-blur">
              <h2 className="text-lg font-semibold text-azeka-navy">{s.h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-azeka-navy2">{s.b}</p>
            </motion.div>
          ))}
        </div>

        {/* CTAs + crawlable cross-link back to Azeka Global */}
        <motion.div {...anim(0.7)} className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link to={HEALTH.cta.to} data-testid="health-cta-enquire" className="inline-flex items-center gap-2 rounded-full bg-azeka-navy px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-navy2 hover:-translate-y-0.5 active:scale-95">
            {HEALTH.cta.label} <ArrowUpRight className="h-4 w-4" />
          </Link>
          <a href="https://azekaglobal.com" data-testid="health-corporate-link" className="inline-flex items-center gap-2 rounded-full border border-azeka-navy/20 bg-white/70 px-7 py-4 text-sm font-semibold text-azeka-navy transition-colors hover:text-azeka-teal">
            <ArrowLeft className="h-4 w-4" /> Corporate &amp; Trade — Azeka Global
          </a>
        </motion.div>

        <motion.p {...anim(0.8)} className="mt-16 text-center text-xs text-azeka-navy2/60">
          © {new Date().getFullYear()} Azeka Health · Part of AZEKA GLOBAL TRADE FZCO
        </motion.p>
      </div>
    </div>
  );
}
